import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  CellValueChangedEvent,
  ColDef,
  ModuleRegistry,
  themeQuartz,
  ValueFormatterParams,
} from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { message } from 'antd';
import { CellDto, RecalcResult, SheetData, TemplateEditorService } from './TemplateEditorService';

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);

// ── Theme ──────────────────────────────────────────────────────────────────────

const gridTheme = themeQuartz.withParams({
  backgroundColor: 'rgb(8,12,28)',
  foregroundColor: 'rgba(255,255,255,0.80)',
  headerBackgroundColor: 'rgba(26,39,68,0.95)',
  headerTextColor: 'rgba(255,255,255,0.50)',
  rowHoverColor: 'rgba(255,255,255,0.04)',
  borderColor: 'rgba(255,255,255,0.08)',
  fontSize: 13,
});

// ── Cell type helpers ──────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getCellStyle = (cellType: string): any => {
  switch (cellType) {
    case 'input':
      return {
        backgroundColor: 'rgba(255,255,255,0.06)',
        borderRight: '1px solid rgba(255,255,255,0.12)',
        color: '#ffffff',
        cursor: 'text',
      };
    case 'output':
      return {
        backgroundColor: 'rgba(242,101,34,0.10)',
        borderRight: '1px solid rgba(242,101,34,0.20)',
        color: '#F26522',
        fontWeight: 600,
      };
    case 'formula':
      return {
        backgroundColor: 'rgba(99,179,237,0.06)',
        borderRight: '1px solid rgba(99,179,237,0.15)',
        color: 'rgba(255,255,255,0.65)',
      };
    case 'header':
      return {
        backgroundColor: 'rgba(26,39,68,0.95)',
        color: 'rgba(255,255,255,0.50)',
        fontWeight: 700,
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      };
    default:
      return {
        backgroundColor: 'rgba(15,20,40,0.60)',
        color: 'rgba(255,255,255,0.80)',
      };
  }
};

// ── Grid builder ───────────────────────────────────────────────────────────────

const buildGridData = (data: SheetData, showFormulas: boolean = false) => {
  const cellMap = new Map<string, CellDto>();
  data.cells.forEach(cell => {
    cellMap.set(`${cell.row},${cell.col}`, cell);
  });

  // Use the header row (cellType='header') for column names; fall back to row 0.
  const headerRow = data.cells.find(c => c.cellType === 'header')?.row ?? 0;

  const headers: string[] = [];
  for (let c = 0; c < data.colCount; c++) {
    const cell = cellMap.get(`${headerRow},${c}`);
    headers.push(cell?.displayValue || String.fromCharCode(65 + c));
  }

  const columnDefs: ColDef[] = headers.map((header, colIdx) => {
    // Detect if this column is a calculated (formula/output) column.
    let isCalcCol = false;
    let repFormula: string | null = null;
    for (const cell of data.cells) {
      if (cell.col === colIdx && (cell.cellType === 'formula' || cell.cellType === 'output')) {
        isCalcCol = true;
        if (!repFormula && cell.formula) repFormula = cell.formula;
        break;
      }
    }
    const truncatedFormula = repFormula
      ? (repFormula.length > 80 ? `${repFormula.slice(0, 80)}...` : repFormula)
      : null;

    return {
      field: `col_${colIdx}`,
      headerName: isCalcCol ? `∑ ${header}` : header,
      headerTooltip: truncatedFormula ?? undefined,
      width: 160,
      editable: (params: any) => {
        const r = params.data?._rowIndex ?? params.node.rowIndex;
        const cell = cellMap.get(`${r},${colIdx}`);
        return cell?.isEditable ?? false;
      },
      cellStyle: (params: any) => {
        const r = params.data?._rowIndex ?? params.node.rowIndex;
        const cell = cellMap.get(`${r},${colIdx}`);
        return getCellStyle(cell?.cellType ?? 'data');
      },
      tooltipValueGetter: isCalcCol
        ? (params: any) => {
          const r = params.data?._rowIndex ?? (params.node?.rowIndex ?? 0);
          const cell = cellMap.get(`${r},${colIdx}`);
          return cell?.formula ? `Formula: ${cell.formula}` : 'Calculated';
        }
        : undefined,
      valueFormatter: (params: ValueFormatterParams) => {
        const r = (params.data as any)?._rowIndex ?? (params.node?.rowIndex ?? 0);
        const cell = cellMap.get(`${r},${colIdx}`);
        if (showFormulas && cell?.formula) return cell.formula;
        return cell?.displayValue ?? (params.value ?? '');
      },
    };
  });

  // Only render data rows — skip metadata and header rows.
  const dataRowNumbers = Array.from(new Set(
    data.cells
      .filter(c => c.cellType !== 'metadata' && c.cellType !== 'header')
      .map(c => c.row)
  )).sort((a, b) => a - b);

  const rowData: Record<string, any>[] = [];
  for (const r of dataRowNumbers) {
    const row: Record<string, any> = { _rowIndex: r };
    for (let c = 0; c < data.colCount; c++) {
      const cell = cellMap.get(`${r},${c}`);
      row[`col_${c}`] = cell?.displayValue ?? '';
    }
    rowData.push(row);
  }

  return { columnDefs, rowData, cellMap };
};

// ── Props ──────────────────────────────────────────────────────────────────────

interface TemplateEditorProps {
  templateId: number;
  fileName: string;
  onClose: () => void;
  onSaved: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  templateId,
  onClose,
  onSaved,
}) => {
  const [sheetData, setSheetData] = useState<SheetData | null>(null);
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [outputSummary, setOutputSummary] = useState<RecalcResult['outputSummary'] | null>(null);
  const showFormulas = false;

  const pendingChanges = useRef<{ sheet: string; row: number; col: number; value: any }[]>([]);
  const allChangesRef = useRef<{ sheet: string; row: number; col: number; value: any }[]>([]);
  const recalcTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gridApiRef = useRef<any>(null);
  const gridRef = useRef<AgGridReact | null>(null);

  useEffect(() => {
    if (!templateId) return;
    setLoading(true);
    setLoadError(false);
    // Pass undefined when no sheet selected so the API returns the first real sheet.
    TemplateEditorService.getSheetData(templateId, activeSheet || undefined)
      .then(data => {
        setSheetData(data);
        // Sync the active tab to the first real sheet on initial load.
        if (!activeSheet) setActiveSheet(data.activeSheet || data.sheetNames[0] || '');
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load template:', err);
        setSheetData(null);
        setLoadError(true);
        setLoading(false);
      });
  }, [templateId, activeSheet]);

  const gridData = useMemo(() => {
    if (!sheetData) return { columnDefs: [], rowData: [], cellMap: new Map<string, CellDto>() };
    return buildGridData(sheetData, showFormulas);
  }, [sheetData, showFormulas]);

  const onCellValueChanged = useCallback((event: CellValueChangedEvent) => {
    const colIdx = parseInt(
      (event.colDef.field ?? 'col_0').replace('col_', ''), 10);
    const rowIdx = (event.node.data?._rowIndex ?? event.rowIndex ?? 0) as number;

    const change = { sheet: activeSheet, row: rowIdx, col: colIdx, value: event.newValue };
    pendingChanges.current.push(change);
    allChangesRef.current.push(change);
    setHasUnsavedChanges(true);

    if (recalcTimer.current) clearTimeout(recalcTimer.current);

    recalcTimer.current = setTimeout(async () => {
      if (pendingChanges.current.length === 0) return;
      const changes = [...pendingChanges.current];
      pendingChanges.current = [];

      setRecalculating(true);
      try {
        const result = await TemplateEditorService.recalculate(
          templateId, activeSheet,
          changes.map(c => ({ row: c.row, col: c.col, value: c.value }))
        );

        result.updatedCells.forEach(cell => {
          const rowNode = gridApiRef.current?.getDisplayedRowAtIndex(cell.row - 1);
          if (rowNode) {
            rowNode.setDataValue(`col_${cell.col}`, cell.displayValue);
          }
        });

        if (result.outputSummary) {
          setOutputSummary(result.outputSummary);
        }
      } catch (err) {
        console.error('Recalc error:', err);
      } finally {
        setRecalculating(false);
      }
    }, 500);
  }, [templateId, activeSheet]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await TemplateEditorService.saveEdits(templateId, allChangesRef.current);
      allChangesRef.current = [];
      setHasUnsavedChanges(false);
      message.success('Saved as new template version');
      onSaved();
    } catch {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'rgba(8,12,28,0.98)',
    }}>

      {/* Top bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        gap: 16,
        flexShrink: 0,
      }}>

        {/* Sheet tabs */}
        <div style={{ display: 'flex', gap: 4, flex: 1 }}>
          {sheetData?.sheetNames.map(name => (
            <button
              key={name}
              onClick={() => setActiveSheet(name)}
              style={{
                padding: '6px 16px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: activeSheet === name ? 600 : 400,
                background: activeSheet === name
                  ? 'rgba(242,101,34,0.20)'
                  : 'rgba(255,255,255,0.06)',
                color: activeSheet === name
                  ? '#F26522'
                  : 'rgba(255,255,255,0.55)',
                transition: 'all 0.15s',
              }}
            >
              {name}
            </button>
          ))}
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex', gap: 12, alignItems: 'center',
          fontSize: 11, color: 'rgba(255,255,255,0.40)',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.20)',
              display: 'inline-block' }}/>
            Input
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2,
              background: 'rgba(99,179,237,0.12)',
              border: '1px solid rgba(99,179,237,0.25)',
              display: 'inline-block' }}/>
            Formula
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2,
              background: 'rgba(242,101,34,0.15)',
              border: '1px solid rgba(242,101,34,0.30)',
              display: 'inline-block' }}/>
            Output
          </span>
        </div>

        {/* Hidden for demo — formula editor tab post-July-13 */}

        {/* Save button */}
        <button
          disabled={saving || !hasUnsavedChanges}
          onClick={handleSave}
          style={{
            padding: '8px 20px',
            background: '#F26522',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: saving || !hasUnsavedChanges ? 'not-allowed' : 'pointer',
            opacity: saving || !hasUnsavedChanges ? 0.5 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          {saving ? 'Saving...' : 'Save Version'}
        </button>
      </div>

      {/* Main area: grid + output panel */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* AG Grid */}
        <div style={{ flex: 1, overflow: 'hidden', height: '100%' }}>
          {loading ? (
            <div style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', height: '100%',
              color: 'rgba(255,255,255,0.35)', fontSize: 13,
            }}>
              Loading template...
            </div>
          ) : loadError || !sheetData ? (
            <div style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', height: '100%',
              color: '#ef4444', fontSize: 13, textAlign: 'center', padding: '0 24px',
            }}>
              Template data unavailable — check template upload and try again.
            </div>
          ) : (
            <AgGridReact
              ref={gridRef}
              rowData={gridData.rowData}
              columnDefs={gridData.columnDefs}
              theme={gridTheme}
              onGridReady={(params) => { gridApiRef.current = params.api; }}
              onCellValueChanged={onCellValueChanged}
              stopEditingWhenCellsLoseFocus
              singleClickEdit
              domLayout="normal"
              rowHeight={36}
              headerHeight={40}
              tooltipShowDelay={300}
              sideBar={true}
              cellSelection={true}
              enableCellTextSelection={true}
              rowGroupPanelShow="always"
              suppressMenuHide={false}
              statusBar={{
                statusPanels: [
                  { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' as const },
                  { statusPanel: 'agTotalRowCountComponent', align: 'center' as const },
                  { statusPanel: 'agAggregationComponent' },
                ],
              }}
            />
          )}
        </div>

        {/* Live Output Panel */}
        <div style={{
          width: 220,
          borderLeft: '1px solid rgba(255,255,255,0.08)',
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          background: 'rgba(8,12,28,0.95)',
          overflowY: 'auto',
          flexShrink: 0,
        }}>
          <div>
            <div style={{
              fontSize: 10, fontWeight: 700,
              color: 'rgba(255,255,255,0.35)',
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              marginBottom: 16,
            }}>
              Live K-1 Output
            </div>

            {recalculating && (
              <div style={{
                fontSize: 11, color: '#F26522',
                marginBottom: 12,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{
                  width: 8, height: 8,
                  borderRadius: '50%',
                  background: '#F26522',
                  display: 'inline-block',
                  animation: 'pulse 1s ease-in-out infinite',
                }}/>
                Recalculating...
              </div>
            )}

            {[
              { key: 'L1',  label: 'Ordinary Income',  icon: '↗' },
              { key: 'L5',  label: 'Interest Income',   icon: '↗' },
              { key: 'L9a', label: 'Net LT Cap Gain',   icon: '↗' },
              { key: 'L13', label: 'Depreciation',      icon: '↘' },
              { key: 'L16', label: 'Foreign Tax',        icon: '→' },
              { key: 'L20', label: 'Ending Capital',    icon: '↗' },
            ].map(line => {
              const val = outputSummary?.k1Lines?.[line.key];
              return (
                <div key={line.key} style={{
                  marginBottom: 16,
                  paddingBottom: 16,
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}>
                  <div style={{
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.35)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    marginBottom: 4,
                  }}>
                    {line.label}
                  </div>
                  <div style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: val != null && val < 0 ? '#ff6b6b' : '#F26522',
                    fontFamily: 'monospace',
                  }}>
                    {val != null
                      ? `${val < 0 ? '-' : ''}$${Math.abs(val / 1_000_000).toFixed(1)}M`
                      : '—'}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Partner count */}
          <div style={{
            marginTop: 'auto',
            padding: '12px 0',
            borderTop: '1px solid rgba(255,255,255,0.06)',
          }}>
            <div style={{
              fontSize: 10,
              color: 'rgba(255,255,255,0.30)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
              Partners
            </div>
            <div style={{
              fontSize: 20,
              fontWeight: 700,
              color: 'rgba(255,255,255,0.70)',
              marginTop: 4,
            }}>
              {outputSummary?.partnerCount ?? '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Pulse animation for recalculating indicator */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default TemplateEditor;
